import type { BootMode } from './types';

export interface StepContent {
  step: number;
  title: string;
  instruction: string;
  coachRail?: string;
}

const BASIC_STEPS: StepContent[] = [
  {
    step: 1,
    title: 'Purge your plate',
    instruction:
      'Write down everything on your plate: to-dos, projects, commitments, problems, opportunities, and long-term goals. Get it all out of your head.',
    coachRail: 'Paper works best for some people, but use whatever captures deliberately for you.',
  },
  {
    step: 2,
    title: 'Tag avoidances, time-sensitive items, and North Star steps',
    instruction:
      'Mark what you are avoiding (A), what is truly time-sensitive with high cost of delay (T), and what moves your North Star goal forward (G). Reword vague items until the next action is obvious.',
    coachRail: 'Identifying avoidances is the single most important move in this routine.',
  },
  {
    step: 3,
    title: 'Rank your A, T, and G items',
    instruction:
      'Stack rank within each tag. Mark your biggest avoidance with 1, most urgent time-sensitive item with 2, and most essential North Star step with 3.',
    coachRail: 'What am I pretending not to know?',
  },
  {
    step: 4,
    title: 'Schedule a timebox for your North Star',
    instruction:
      'Block 30 minutes today (or as soon as possible) to think about or work on your North Star next step. Feed your future — commit to allocation of time, not completion of the whole goal.',
  },
  {
    step: 5,
    title: 'Work on your big avoidance',
    instruction:
      'Take a deep breath and dig in. Focus on your top avoidance for ~30 minutes before anything else. Chip away or complete it if you can.',
    coachRail: 'Completing an avoidance releases energy for everything else.',
  },
];

const ADVANCED_STEPS: StepContent[] = [
  {
    step: 1,
    title: 'Review backlog and purge',
    instruction:
      'Review your existing priorities and backlog. Add anything new that has come to mind since your last Morning Boot.',
  },
  {
    step: 2,
    title: 'Role-based scan',
    instruction:
      'Think of key stakeholders in your life and work. Does each role jog any forgotten commitments or items?',
    coachRail: 'Check personal and professional roles — commitments in one area affect the other.',
  },
  {
    step: 3,
    title: 'Calendar and communications review',
    instruction:
      'Review recent and upcoming calendar events. Scan sent messages for commitments you may have skipped.',
  },
  {
    step: 4,
    title: 'Research, read, and think items',
    instruction:
      'Add items that contribute to learning and skills development: articles, courses, podcasts, or thinking time.',
  },
  {
    step: 5,
    title: 'Commitments, dependencies, levers, and questions',
    instruction:
      'Highlight commitments, dependencies, leverage items that unblock other work, and open questions worth capturing.',
  },
  {
    step: 6,
    title: 'Tag avoidances, time-sensitive items, and North Star steps',
    instruction: BASIC_STEPS[1].instruction,
    coachRail: BASIC_STEPS[1].coachRail,
  },
  {
    step: 7,
    title: 'Rank your A, T, and G items',
    instruction: BASIC_STEPS[2].instruction,
    coachRail: BASIC_STEPS[2].coachRail,
  },
  {
    step: 8,
    title: 'Schedule a timebox for your North Star',
    instruction: BASIC_STEPS[3].instruction,
  },
  {
    step: 9,
    title: 'Work on your big avoidance',
    instruction: BASIC_STEPS[4].instruction,
    coachRail: BASIC_STEPS[4].coachRail,
  },
];

export function getStepsForMode(mode: BootMode): StepContent[] {
  return mode === 'advanced' ? ADVANCED_STEPS : BASIC_STEPS;
}

export function getMaxStep(mode: BootMode): number {
  return mode === 'advanced' ? 9 : 5;
}

export function getStepContent(mode: BootMode, step: number): StepContent | undefined {
  return getStepsForMode(mode).find((s) => s.step === step);
}

export const TAG_LABELS: Record<'A' | 'T' | 'G', { label: string; short: string; description: string }> = {
  A: {
    label: 'Avoidance',
    short: 'A',
    description: 'What you are avoiding, ignoring, or procrastinating on',
  },
  T: {
    label: 'Time-sensitive',
    short: 'T',
    description: 'High cost of delay — waiting makes it worse or costs control of the timeline',
  },
  G: {
    label: 'North Star step',
    short: 'G',
    description: 'Progress toward your most important long-term strategic goal',
  },
};

export const INTERRUPT_OPTIONS = [
  {
    id: 'slot',
    label: 'Understand and slot it',
    description: 'Take a moment to see how the new item fits your existing hierarchy.',
  },
  {
    id: 'guesstimate',
    label: 'Guesstimate priority',
    description: 'Adjust your existing priorities to accommodate the new item.',
  },
  {
    id: 'backlog',
    label: 'Add to backlog',
    description: 'Stay focused on what you already defined; evaluate later.',
  },
];
